import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings, CreditCard, Building2, Check, Crown, Zap, Star, Upload, Image, X, Edit2, Save, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    business_name: '',
    phone: ''
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPlans();
    checkPaymentStatus();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        business_name: user.business_name || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!profileData.business_name.trim()) {
      toast.error('El nombre del negocio es obligatorio');
      return;
    }

    setSavingProfile(true);
    try {
      await axios.patch(`${API}/merchant/profile`, {
        business_name: profileData.business_name.trim(),
        phone: profileData.phone.trim()
      });
      await refreshUser();
      setEditingProfile(false);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const cancelEdit = () => {
    setProfileData({
      business_name: user?.business_name || '',
      phone: user?.phone || ''
    });
    setEditingProfile(false);
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    try {
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        if (attempts >= maxAttempts) {
          toast.info('Verifica tu email para confirmar el pago');
          return;
        }

        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          toast.success('¡Plan actualizado correctamente!');
          await refreshUser();
          return;
        }

        attempts++;
        setTimeout(poll, pollInterval);
      };

      poll();
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'free' || planId === user?.plan) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        plan: planId,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no puede superar los 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/upload/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      await refreshUser();
      toast.success('Logo actualizado correctamente');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error al subir el logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'growth': return <Zap className="w-6 h-6" />;
      case 'scale': return <Crown className="w-6 h-6" />;
      case 'elite': return <Crown className="w-6 h-6 text-yellow-500" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  const getLogoUrl = () => {
    if (!user?.logo_url) return null;
    if (user.logo_url.startsWith('http')) return user.logo_url;
    return `${BACKEND_URL}${user.logo_url}`;
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configuración</h1>
        <p className="text-slate-600">Gestiona tu cuenta y suscripción</p>
      </div>

      {/* Account Info */}
      <motion.div 
        className="card-dashboard"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-primary" />
            <h2 className="text-lg font-bold text-slate-900">Información del negocio</h2>
          </div>
          {!editingProfile ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setEditingProfile(true)}
              data-testid="edit-profile-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Editar
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={cancelEdit}
              >
                Cancelar
              </Button>
              <Button 
                size="sm"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="btn-primary"
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo Section */}
          <div className="md:col-span-2">
            <Label className="text-slate-500 mb-2 block">Logo del negocio</Label>
            <div className="flex items-center gap-4">
              {getLogoUrl() ? (
                <div className="relative">
                  <img 
                    src={getLogoUrl()} 
                    alt="Logo del negocio" 
                    className="w-24 h-24 object-contain rounded-xl border border-slate-200 bg-white p-2"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center bg-slate-50">
                  <Image className="w-8 h-8 text-slate-400" />
                  <span className="text-xs text-slate-400 mt-1">Sin logo</span>
                </div>
              )}
              <div>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingLogo ? 'Subiendo...' : (getLogoUrl() ? 'Cambiar logo' : 'Subir logo')}
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Este logo aparecerá cuando tus clientes escaneen el QR
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Business Name */}
          <div>
            <Label className="text-slate-500">Nombre del negocio</Label>
            {editingProfile ? (
              <Input
                value={profileData.business_name}
                onChange={(e) => setProfileData({ ...profileData, business_name: e.target.value })}
                placeholder="Nombre de tu negocio"
                className="mt-1"
                data-testid="edit-business-name"
              />
            ) : (
              <p className="font-medium text-slate-900">{user?.business_name}</p>
            )}
          </div>

          {/* Email (read-only) */}
          <div>
            <Label className="text-slate-500">Email (usuario de acceso)</Label>
            <p className="font-medium text-slate-900">{user?.email}</p>
            {editingProfile && (
              <p className="text-xs text-slate-400 mt-1">El email no se puede modificar</p>
            )}
          </div>

          {/* Phone */}
          <div>
            <Label className="text-slate-500">Teléfono</Label>
            {editingProfile ? (
              <Input
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                placeholder="Ej: 612 345 678"
                className="mt-1"
                data-testid="edit-phone"
              />
            ) : (
              <p className="font-medium text-slate-900">{user?.phone || 'No configurado'}</p>
            )}
          </div>

          {/* Plan */}
          <div>
            <Label className="text-slate-500">Plan actual</Label>
            <div className="flex items-center gap-2">
              <Badge className="capitalize">{user?.plan}</Badge>
              {user?.status === 'trial' && user?.trial_ends_at && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  Trial - {Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24))} días
                </Badge>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Subscription Plans */}
      <motion.div 
        className="space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-3">
          <CreditCard className="w-6 h-6 text-primary" />
          <h2 className="text-lg font-bold text-slate-900">Planes de suscripción</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan, i) => {
            const isCurrentPlan = user?.plan === plan.id;
            const isUpgrade = plan.id !== 'free' && !isCurrentPlan;

            return (
              <motion.div
                key={plan.id}
                className={`rounded-2xl p-6 border-2 transition-all ${
                  isCurrentPlan 
                    ? 'border-primary bg-primary/5' 
                    : 'border-slate-200 bg-white hover:border-primary/50'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                data-testid={`plan-card-${plan.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    isCurrentPlan ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {getPlanIcon(plan.id)}
                  </div>
                  {isCurrentPlan && (
                    <Badge className="bg-primary">Plan actual</Badge>
                  )}
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-1">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-extrabold text-slate-900">{plan.price}€</span>
                  <span className="text-slate-500">/mes</span>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className={`w-4 h-4 ${isCurrentPlan ? 'text-primary' : 'text-slate-400'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {isUpgrade && (
                  <Button 
                    className="w-full btn-primary"
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={loading}
                    data-testid={`upgrade-${plan.id}-btn`}
                  >
                    {loading ? 'Procesando...' : `Cambiar a ${plan.name}`}
                  </Button>
                )}

                {isCurrentPlan && plan.id !== 'free' && (
                  <Button variant="outline" className="w-full" disabled>
                    Plan activo
                  </Button>
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Usage Stats */}
      <motion.div 
        className="card-dashboard"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center gap-3 mb-4">
          <Settings className="w-6 h-6 text-primary" />
          <h2 className="text-lg font-bold text-slate-900">Uso del mes</h2>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <p className="font-bold text-slate-900">{user?.stamps_this_month || 0} sellos</p>
            <p className="text-sm text-slate-500">
              de {plans.find(p => p.id === user?.plan)?.stamps_limit || 100} disponibles
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">
              {Math.round(((user?.stamps_this_month || 0) / (plans.find(p => p.id === user?.plan)?.stamps_limit || 100)) * 100)}%
            </p>
            <p className="text-sm text-slate-500">usado</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SettingsPage;
